import { SentryIssue, SentryIssueDetails } from "../../api/sentryIssues";
import { TicketPriority } from "../Tickets/types";
import { CreateTicketDraft } from "../Tickets/components/detail/CreateTicketModal";

/** Every ticket raised from Sentry carries this, so they can be filtered. */
export const SENTRY_TICKET_LABEL = "sentry";

/** Opens a ticket in Klydoscope in a new tab, leaving the Sentry list as is. */
export const openTicket = (ticketId: string) =>
  window.open(`/admin/tickets?ticket=${ticketId}`, "_blank", "noreferrer");

/** Sentry project slug -> the Klydoscope repo label it belongs to. */
const LABEL_BY_PROJECT: Record<string, string> = {
  "node-nestjs": "api",
  "klydo-functions": "api",
  studio: "studio",
  editor: "editor",
  "mobile-application": "mobile",
  "shopify-storefront": "shopify",
};

const repoLabel = (projectSlug: string): string | null => {
  if (LABEL_BY_PROJECT[projectSlug]) return LABEL_BY_PROJECT[projectSlug];
  if (/backoffice|admin/.test(projectSlug)) return "admin";
  if (/clock|android/.test(projectSlug)) return "clock";
  return null;
};

const priorityFor = (issue: SentryIssue): TicketPriority => {
  if (issue.level === "fatal") return "critical";
  if (issue.level === "error") return issue.isUnhandled ? "high" : "medium";
  return "low";
};

const MAX_TITLE_LENGTH = 120;
const MAX_STACK_FRAMES = 10;

const line = (label: string, value: string | number | null | undefined) =>
  value === null || value === undefined || value === ""
    ? null
    : `${label}: ${value}`;

/** Prefills the create-ticket form from a Sentry issue and its latest event. */
export const buildSentryTicketDraft = (
  issue: SentryIssue,
  latestEvent: SentryIssueDetails["latestEvent"] | undefined,
): CreateTicketDraft => {
  const title = `[${issue.shortId}] ${issue.title}`;

  const details = [
    line("Sentry", issue.permalink),
    line("Issue", issue.shortId),
    line("Project", issue.projectSlug),
    line("Domain", issue.domain),
    line(
      "Level",
      `${issue.level} (${issue.isUnhandled ? "unhandled" : "handled"})`,
    ),
    line("Culprit", issue.culprit),
    line(
      "Error",
      [issue.errorType, issue.errorValue].filter(Boolean).join(": "),
    ),
    line("Events (all time)", issue.lifetimeCount),
    line("Users affected", issue.userCount),
    line("First seen", issue.firstSeen),
    line("Last seen", issue.lastSeen),
    line("Release", latestEvent?.release),
    line("Environment", latestEvent?.environment),
    line(
      "Request",
      [latestEvent?.requestMethod, latestEvent?.requestUrl]
        .filter(Boolean)
        .join(" "),
    ),
  ].filter((entry): entry is string => entry !== null);

  // Our own frames are what someone will act on; fall back to all of them when
  // the event has none marked in-app.
  const frames = latestEvent?.frames ?? [];
  const inAppFrames = frames.filter((frame) => frame.isInApp);
  const stack = (inAppFrames.length ? inAppFrames : frames)
    .slice(0, MAX_STACK_FRAMES)
    .map(
      (frame) =>
        `  ${frame.functionName ?? "<anonymous>"} (${frame.filename ?? "unknown"}${
          frame.lineNumber ? `:${frame.lineNumber}` : ""
        })`,
    );

  const description = [
    details.join("\n"),
    stack.length ? `Stack (latest event):\n${stack.join("\n")}` : null,
  ]
    .filter(Boolean)
    .join("\n\n");

  const label = repoLabel(issue.projectSlug);

  return {
    title:
      title.length > MAX_TITLE_LENGTH
        ? `${title.slice(0, MAX_TITLE_LENGTH - 1)}…`
        : title,
    description,
    type: "error",
    priority: priorityFor(issue),
    labels: label ? [label, SENTRY_TICKET_LABEL] : [SENTRY_TICKET_LABEL],
    url: issue.permalink,
  };
};
